import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../lib/format';

/**
 * Hiện URL đăng ký động để admin chép sang LMS.
 *
 * Phải là dialog chứ không phải toast: URL dài, dùng MỘT LẦN, và người dùng
 * cần thời gian chuyển sang tab Moodle để dán. Đóng mất là phải sinh vé mới.
 */
export function RegistrationTicketDialog({
  registerUrl,
  expiresAt,
  onClose,
}: {
  registerUrl: string;
  expiresAt: string;
  onClose: () => void;
}): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  /**
   * `navigator.clipboard` chỉ có ở secure context. http://localhost tính là
   * secure, nhưng máy dev truy cập qua IP LAN thì KHÔNG — ở đó API này
   * `undefined` và nút sẽ im lặng không làm gì nếu thiếu nhánh dự phòng.
   */
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(registerUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt(t('lti.ticketCopyFallback'), registerUrl);
    }
  };

  return (
    <dialog ref={dialogRef} className="dialog dialog--wide" onClose={onClose}>
      <h2>{t('lti.ticketHeading')}</h2>
      <p className="hint">{t('lti.ticketIntro')}</p>

      <label className="field">
        <span>{t('lti.ticketUrl')}</span>
        <input type="text" readOnly value={registerUrl} onFocus={(e) => e.target.select()} />
      </label>

      <p className="hint">{t('lti.ticketExpiresAt', { at: formatDateTime(expiresAt, locale) })}</p>
      <p className="hint">{t('lti.ticketWarning')}</p>

      <footer className="dialog__footer">
        <button type="button" onClick={() => void copy()}>
          {copied ? t('lti.ticketCopied') : t('lti.ticketCopy')}
        </button>
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.close')}
        </button>
      </footer>
    </dialog>
  );
}
